import { Link } from "@tanstack/react-router";
import { Combine, Link2, Printer } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { MetaBadge } from "@/services/meta-orders";
import type { MetaLookup, MetaPickSource } from "@/services/fulfillment";

/** "M-12 · Cherokee + Bebushka" — svuda gdje se meta narudžba pojavljuje vide se uključeni shopovi. */
export function metaTitle(number: string, storeNames: string[]): string {
  return storeNames.length ? `${number} · ${storeNames.join(" + ")}` : number;
}

const MEMBER_CLASS = "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300";
const CANDIDATE_CLASS = "border-amber-500/40 bg-amber-400/20 text-amber-800 dark:text-amber-300";

/** Oznaka meta narudžbe (naljepnice, pick, pack). */
export function MetaOrderTag({ number, storeNames, className }: { number: string; storeNames: string[]; className?: string }) {
  return (
    <Badge variant="outline" className={cn(MEMBER_CLASS, "tabular-nums", className)}>
      <Combine />
      {metaTitle(number, storeNames)}
    </Badge>
  );
}

/** "štampano" kod meta naljepnice — ista meta narudžba se vidi iz više shopova. */
export function LabelPrintedTag({ className }: { className?: string }) {
  return (
    <Badge
      variant="outline"
      className={cn("border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400", className)}
      title="Naljepnica za ovu spojenu pošiljku je već štampana"
    >
      <Printer />
      štampano
    </Badge>
  );
}

/** Oznaka u listi narudžbi; klik vodi na "Spajanje pošiljki" fokusirano na grupu ili meta narudžbu. */
export function MetaListBadge({ badge }: { badge: MetaBadge }) {
  if (badge.kind === "candidate") {
    const shops = badge.storeNames.length ? ` (${badge.storeNames.join(", ")})` : "";
    return (
      <Badge asChild variant="outline" className={cn(CANDIDATE_CLASS, "hover:bg-amber-400/30")}>
        <Link
          to="/fulfillment/merge"
          search={{ group: badge.groupId }}
          title="Isti kupac ima još neposlanih narudžbi — otvori Spajanje pošiljki"
        >
          <Link2 />
          još {badge.otherCount}{shops}
        </Link>
      </Badge>
    );
  }
  return (
    <Badge asChild variant="outline" className={cn(MEMBER_CLASS, "tabular-nums hover:bg-violet-500/20")}>
      <Link
        to="/fulfillment/merge"
        search={{ meta: badge.number }}
        title={`Dio spojene pošiljke ${badge.number} — otvori Spajanje pošiljki`}
      >
        <Combine />
        {metaTitle(badge.number, badge.storeNames)}
      </Link>
    </Badge>
  );
}

/** "Cherokee #10452" */
export function memberLabel(storeName: string | undefined, number: string): string {
  return storeName ? `${storeName} #${number}` : `#${number}`;
}

/** Zaglavlje zbirne liste na picku i packu: oznaka meta narudžbe i članovi. */
export function MetaLookupHeader({ meta, showPacked = false }: { meta: MetaLookup; showPacked?: boolean }) {
  return (
    <div className="flex flex-col gap-1.5">
      <MetaOrderTag number={meta.number} storeNames={meta.storeNames} className="h-6 text-sm" />
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground tabular-nums">
        {meta.members.map((m) => (
          <span key={`${m.storeId}:${m.orderId}`} className={cn(showPacked && m.packed && "text-green-700 dark:text-green-400")}>
            {memberLabel(m.storeName, m.number)}
            {showPacked && m.packed && " · spakovano"}
          </span>
        ))}
      </div>
    </div>
  );
}

/** "Cherokee #10452 ×1, Bebushka #3381 ×2" ispod stavke zbirne liste. */
export function MetaSourcesLine({ sources }: { sources?: MetaPickSource[] }) {
  if (!sources?.length) return null;
  return (
    <p className="text-[11px] text-violet-700 dark:text-violet-300 tabular-nums">
      {sources.map((s) => `${memberLabel(s.storeName, s.orderNumber)} ×${s.quantity}`).join(", ")}
    </p>
  );
}
